<script>
  import { onMount, onDestroy } from 'svelte';
  import { sessionStore } from '../stores';
  import Modal from '../components/Modal.svelte';
  import { getFeed, feedAction, feedImageUrl, shareUrl } from '../utils/communityApi';
  import { requestLogin } from '../utils/authSession';
  import { request } from '../utils/request';
  import { launchStyle } from '../utils/editorLaunch';
  import { resolveStyle } from '../utils/styleCatalog';
  let items = [], sort = 'popular_week', query = '', scope = '', style = '', limit = 40;
  let loading = false, error = '', selected = null, zoom = 100, busy = false, message = '';
  let report = null, reason = '부적절한 콘텐츠', detail = '', reportAuthor = false;
  let revision = 0, controller;
  async function load() {
    const version = ++revision;
    controller?.abort(); controller = new AbortController();
    loading = true; error = '';
    try {
      const result = await getFeed({ sort, scope, q: query.trim(), style, limit }, controller.signal);
      if (version === revision) items = result;
    } catch (e) { if (e.name !== 'AbortError' && version === revision) error = e.message; }
    finally { if (version === revision) loading = false; }
  }
  async function filter(event) {
    if (['liked', 'bookmarked', 'hidden'].includes(sort) || scope === 'mine') {
      if (!await requestLogin('내 커뮤니티 활동을 보려면 로그인해 주세요.')) { sort = 'popular_week'; scope = ''; }
    }
    limit = 40; selected = null; await load();
  }
  async function act(item, action, method = 'POST', body) {
    if (busy) return;
    if (!await requestLogin('커뮤니티에 참여하려면 로그인해 주세요.')) return;
    busy = true; error = '';
    try {
      const result = await feedAction(item, action, method, body);
      if (!action || action === 'hide' || action === 'report') { selected = null; await load(); }
      else if (result.item) { items = items.map(old => old.id === item.id ? result.item : old); if (selected?.id === item.id) selected = result.item; }
    } catch (e) { error = e.message; } finally { busy = false; }
  }
  async function share(item) {
    try {
      const url = shareUrl(item);
      if (navigator.share) await navigator.share({ title: `${item.style} · E-RION`, url });
      else { await navigator.clipboard.writeText(url); message = '공유 링크를 복사했어요.'; }
    } catch (e) { if (e.name !== 'AbortError') message = '공유 링크를 복사하지 못했습니다.'; }
  }
  async function submitReport() {
    if (!await requestLogin('신고하려면 로그인해 주세요.')) return;
    if (busy) return;
    busy = true;
    try {
      if (reportAuthor) await request(`/api/community/users/${report.author_id}/report`, { method: 'POST', body: { feed_image_id: report.id, reason, detail } });
      else await feedAction(report, 'report', 'POST', { reason, detail });
      report = null; selected = null; message = '신고가 접수되었습니다.'; await load();
    } catch (e) { error = e.message; } finally { busy = false; }
  }
  function next(direction) {
    const index = items.findIndex(item => item.id === selected?.id);
    const item = items[index + direction]; if (item) { selected = item; zoom = 100; }
  }
  function keydown(e) {
    if (!selected || report || ['INPUT', 'SELECT', 'TEXTAREA'].includes(e.target.tagName)) return;
    if (e.key === 'ArrowLeft') next(-1); if (e.key === 'ArrowRight') next(1);
  }
  onMount(() => { load();
    const id = new URLSearchParams(window.location.search).get('image');
    if (id) request(`/api/feed_images/${encodeURIComponent(id)}`, { authenticated: $sessionStore.loggedIn }).then(item => selected = item).catch(e => error = e.message);
    const recovered = () => load(); document.addEventListener('serviceRecovered', recovered);
    return () => document.removeEventListener('serviceRecovered', recovered);
  });
  onDestroy(() => { revision++; controller?.abort(); });
</script>
<svelte:window on:keydown={keydown} />
<div class="community">
  <h1>커뮤니티</h1><p>다른 반려견의 작품을 둘러보고 마음에 드는 스타일로 만들어 보세요.</p>
  <form class="filters" on:submit|preventDefault={filter}>
    <select aria-label="정렬" bind:value={sort} on:change={filter}>
      <option value="popular_week">이번 주 인기</option><option value="new">최신 작품</option><option value="popular">전체 인기</option>
      <option value="liked">좋아요한 작품</option><option value="bookmarked">저장한 작품</option><option value="hidden">숨긴 작품</option>
    </select>
    <select aria-label="게시물 범위" bind:value={scope} on:change={filter}><option value="">전체 작품</option><option value="user_posts">사용자 작품</option><option value="mine">내 게시물</option></select>
    <input aria-label="스타일 필터" placeholder="스타일 이름" bind:value={style} maxlength="80" />
    <input aria-label="커뮤니티 검색" placeholder="작품·닉네임 검색" bind:value={query} maxlength="80" /><button type="submit">검색</button>
  </form>
  {#if error}<p role="alert">{error} <button type="button" on:click={load}>다시 시도</button></p>{/if}
  {#if message}<p role="status">{message}</p>{/if}
  {#if loading}<p role="status">작품을 불러오는 중...</p>{/if}
  {#if !loading && !items.length && !error}<p class="empty">아직 작품이 없어요. 갤러리에서 내 작품을 공유해 보세요.</p>{/if}
  <div class="grid">
    {#each items as item (item.id)}
      <article>
        <button class="artwork" type="button" on:click={() => { selected = item; zoom = 100; }}><img src={feedImageUrl(item)} alt={`${item.style} 작품`} loading="lazy" /></button>
        <div class="meta"><strong>{item.style}</strong><span>{item.author_name || 'E-RION'}</span></div>
        <div class="actions">
          <button disabled={busy || item.is_owner} aria-pressed={item.liked_by_me} on:click={() => act(item, 'like', item.liked_by_me ? 'DELETE' : 'POST')}>{item.liked_by_me ? '♥' : '♡'} {item.like_count}</button>
          <button disabled={busy} aria-pressed={item.bookmarked_by_me} on:click={() => act(item, 'bookmark', item.bookmarked_by_me ? 'DELETE' : 'POST')}>{item.bookmarked_by_me ? '저장됨' : '저장'}</button>
          {#if resolveStyle(item)}<button on:click={() => launchStyle(item)}>이 스타일로 만들기</button>{/if}
        </div>
      </article>
    {/each}
  </div>
  {#if items.length >= limit && limit < 200}<button class="more" disabled={loading} on:click={() => { limit += 40; load(); }}>더 보기</button>{/if}
</div>
{#if selected && !report}
  <Modal label={`${selected.style} 작품`} on:close={() => selected = null}>
    <div class="viewer"><img src={feedImageUrl(selected)} alt={selected.style} style:width={`${zoom}%`} /></div>
    <div class="actions viewer-actions">
      <button on:click={() => next(-1)}>이전</button><label>확대 <input type="range" min="100" max="250" step="25" bind:value={zoom} /></label><button on:click={() => next(1)}>다음</button>
    </div>
    <h2>{selected.style}</h2><p>{selected.author_name || 'E-RION'}</p>
    <div class="actions">
      {#if resolveStyle(selected)}<button on:click={() => launchStyle(selected)}>이 스타일로 만들기</button>{/if}
      <button on:click={() => share(selected)}>링크 공유</button>
      {#if selected.is_owner}
        <button disabled={busy} on:click={() => { if (confirm('이 작품의 커뮤니티 공개를 취소할까요? 갤러리의 원본은 유지됩니다.')) act(selected, '', 'DELETE'); }}>공개 취소</button>
      {:else}
        <button disabled={busy} on:click={() => act(selected, 'hide', selected.hidden_by_me ? 'DELETE' : 'POST')}>{selected.hidden_by_me ? '숨김 해제' : '숨기기'}</button>
        <button on:click={() => { report = selected; reportAuthor = false; }}>게시물 신고</button>
        {#if selected.author_id}<button on:click={() => { report = selected; reportAuthor = true; }}>작성자 신고</button>{/if}
      {/if}
    </div>
    {#if error}<p role="alert">{error}</p>{/if}
    {#if message}<p role="status">{message}</p>{/if}
  </Modal>
{/if}
{#if report}
  <Modal label={reportAuthor ? '작성자 신고' : '게시물 신고'} closable={!busy} on:close={() => report = null}>
    <h2>{reportAuthor ? '작성자 신고' : '게시물 신고'}</h2>
    <form class="report-form" on:submit|preventDefault={submitReport}>
      <label>신고 사유 <select bind:value={reason}><option>부적절한 콘텐츠</option><option>스팸 및 광고</option><option>권리 침해</option><option>괴롭힘</option><option>기타</option></select></label>
      <label>상세 설명 <textarea bind:value={detail} maxlength="500" rows="4"></textarea></label>
      {#if error}<p role="alert">{error}</p>{/if}<button disabled={busy} type="submit">{busy ? '접수 중...' : '신고하기'}</button>
    </form>
  </Modal>
{/if}
<style>
  .community { padding: 24px 0 60px; } h1 { color: #e56831; } p { line-height: 1.7; }
  .filters, .actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; } .filters { margin: 24px 0; }
  button, select, input, textarea { background: transparent; color: inherit; border: 1px solid #8885; padding: 10px 12px; border-radius: 10px; font: inherit; }
  select option { background: #fff; color: #333; } button { cursor: pointer; } button:disabled { opacity: .5; cursor: default; } button[aria-pressed=true] { color: #e56831; border-color: #e56831; }
  .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(min(260px, 100%), 1fr)); gap: 22px; }
  article { border: 1px solid #8883; border-radius: 18px; overflow: hidden; } .artwork { width: 100%; border: 0; border-radius: 0; padding: 0; display: block; }
  .artwork img { width: 100%; aspect-ratio: 4 / 5; object-fit: cover; display: block; } .meta { display: grid; gap: 6px; padding: 14px; } .meta span { font-size: .85rem; opacity: .7; }
  article .actions { padding: 0 14px 14px; } .actions button { font-size: .85rem; padding: 7px 10px; } .more { display: block; margin: 28px auto; }
  .viewer { overflow: auto; max-height: 60dvh; text-align: center; } .viewer img { display: block; max-width: none; margin: auto; object-fit: contain; }
  .viewer-actions { justify-content: space-between; margin-top: 12px; } .viewer-actions label { display: flex; align-items: center; gap: 8px; }
  .report-form, .report-form label { display: grid; gap: 12px; } .empty { padding: 50px; text-align: center; } [role=alert] { color: #cc4935; }
</style>
